import { useEffect, useState } from "react";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, getToken } from "../../lib/api";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function AdminBackupRestore() {
  const [tables, setTables] = useState([]);
  const [selected, setSelected] = useState([]);
  const [history, setHistory] = useState([]);
  const [file, setFile] = useState(null);
  const [mode, setMode] = useState("insert_ignore");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    try {
      const [tableData, historyData] = await Promise.all([
        api("/api/admin/backups/tables"),
        api("/api/admin/backups/history"),
      ]);
      setTables(tableData.tables || []);
      setSelected((tableData.tables || []).map((item) => item.table));
      setHistory(historyData || []);
    } catch (err) {
      setError(err.message);
    }
  }

  useEffect(() => { load(); }, []);

  function toggle(table) {
    setSelected((prev) => prev.includes(table) ? prev.filter((item) => item !== table) : [...prev, table]);
  }

  async function exportBackup() {
    setError("");
    setMessage("");
    const res = await fetch(`${API_BASE}/api/admin/backups/export`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${getToken()}`,
      },
      body: JSON.stringify({ tables: selected }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      setError(data.error || "Backup export failed");
      return;
    }
    const blob = await res.blob();
    const disposition = res.headers.get("content-disposition") || "";
    const match = disposition.match(/filename="?([^";]+)"?/);
    const filename = match?.[1] || `fpb-backup-${Date.now()}.json`;
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    setMessage("Backup exported successfully.");
    load();
  }

  async function restoreBackup(e) {
    e.preventDefault();
    if (!file) return setError("Select a JSON backup file first.");
    if (!window.confirm("Restore selected tables from this backup file? This should only be done after exporting a fresh backup.")) return;

    const form = new FormData();
    form.append("backup", file);
    form.append("mode", mode);
    form.append("tables", JSON.stringify(selected));

    const res = await fetch(`${API_BASE}/api/admin/backups/restore`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getToken()}` },
      body: form,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return setError(data.error || "Restore failed");
    setMessage(`Restore completed: ${data.total_rows || 0} rows processed.`);
    setFile(null);
    load();
  }

  return (
    <AdminLayout title="Backup & Restore" subtitle="Export CMS data as JSON and restore selected CMS tables when needed.">
      {message && <div className="mb-4 rounded-xl bg-emerald-50 p-4 font-semibold text-emerald-700">{message}</div>}
      {error && <div className="mb-4 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h2 className="text-xl font-black text-slate-950">Select Tables</h2>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {tables.map((item) => (
              <label key={item.table} className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 p-3">
                <span>
                  <span className="block font-bold text-slate-800">{item.table}</span>
                  <span className="text-xs text-slate-500">{item.rows_count} rows</span>
                </span>
                <input type="checkbox" checked={selected.includes(item.table)} onChange={() => toggle(item.table)} />
              </label>
            ))}
          </div>
          <button onClick={exportBackup} className="mt-6 rounded-xl bg-emerald-700 px-5 py-3 font-bold text-white">Download Backup JSON</button>
        </section>

        <section className="rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <h2 className="text-xl font-black text-slate-950">Restore Backup</h2>
          <p className="mt-2 text-sm text-slate-600">Use only backup JSON files exported from this CMS.</p>
          <form onSubmit={restoreBackup} className="mt-5 space-y-4">
            <input type="file" accept="application/json,.json" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full rounded-xl border border-slate-200 p-3" />
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full rounded-xl border border-slate-200 p-3">
              <option value="insert_ignore">Insert missing rows only</option>
              <option value="replace">Replace matching rows</option>
            </select>
            <button className="rounded-xl bg-slate-900 px-5 py-3 font-bold text-white">Restore Selected Tables</button>
          </form>
        </section>
      </div>

      <section className="mt-8 rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        <h2 className="text-xl font-black text-slate-950">Backup History</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 text-xs uppercase tracking-wider text-slate-600"><tr><th className="p-4">Name</th><th className="p-4">Tables</th><th className="p-4">Rows</th><th className="p-4">By</th><th className="p-4">Date</th></tr></thead>
            <tbody>{history.map((item) => <tr key={item.id} className="border-t"><td className="p-4 font-semibold">{item.backup_name}</td><td className="p-4">{item.tables_count}</td><td className="p-4">{item.rows_count}</td><td className="p-4">{item.creator_name || item.creator_email || "Admin"}</td><td className="p-4">{new Date(item.created_at).toLocaleString()}</td></tr>)}</tbody>
          </table>
        </div>
      </section>
    </AdminLayout>
  );
}
